import { matchPath } from 'react-router-dom';
import { t } from '~/i18n';
import routes from './routes';

const HELP_CENTER_URL = 'https://help.ixicare.com';

// The help center ships fewer languages than the dashboard; the rest fall back to its English
// root. Keep in sync with LANGUAGES in help-center/docusaurus.config.ts.
const HELP_CENTER_LANGUAGES = new Set(['en', 'nl', 'fr', 'de', 'it', 'es', 'pt', 'da', 'no', 'sv', 'fi', 'is', 'tr']);

interface HelpArticle {
  /** Docs path under the help-center root, e.g. `handle-an-alarm/groups`. */
  path: string;
  /** What the article is about, named the way the dashboard names it. */
  topic: () => string;
}

// First match wins, so sub-routes that need a different article go before their parent's `/*`.
const ARTICLES: [pattern: string, article: HelpArticle][] = [
  [routes.dashboard, { path: 'dashboard/overview', topic: () => t('menu.dashboard.dashboard') }],
  [`${routes.alerts}/*`, { path: 'handle-an-alarm/handling-alarms', topic: () => t('menu.dashboard.alerts') }],
  [`${routes.residents}/*`, { path: 'setup-a-device/alarm-sender', topic: () => t('menu.dashboard.residents') }],
  [
    `${routes.healthcareProviders}/*`,
    { path: 'handle-an-alarm/alarm-receiver', topic: () => t('menu.dashboard.healthcareProviders') },
  ],
  [routes.hcpGroups, { path: 'handle-an-alarm/groups', topic: () => t('menu.dashboard.hcpGroups') }],
  [routes.configuration, { path: 'dashboard/settings', topic: () => t('menu.dashboard.configuration') }],
  [routes.sites, { path: 'setup-a-site/site', topic: () => t('menu.dashboard.settingsSites') }],
  [`${routes.sites}/create`, { path: 'setup-a-site/site', topic: () => t('menu.dashboard.settingsSites') }],
  [`${routes.sites}/*`, { path: 'setup-a-site/floor-plans', topic: () => t('global.floorPlans') }],
  [routes.deviceHealth, { path: 'setup-a-device/device-health', topic: () => t('menu.dashboard.deviceHealth') }],
  [routes.deviceOverview, { path: 'setup-a-device/device', topic: () => t('alerts.status.devices') }],
  [
    `${routes.deviceOverview}/*`,
    { path: 'setup-a-device/device-configuration', topic: () => t('menu.dashboard.deviceConfiguration') },
  ],
  [routes.statistics, { path: 'dashboard/statistics', topic: () => t('menu.dashboard.statistics') }],
];

const helpCenterRoot = (language: string) =>
  HELP_CENTER_LANGUAGES.has(language) && language !== 'en' ? `${HELP_CENTER_URL}/${language}` : HELP_CENTER_URL;

/** The help-center link and tooltip for a dashboard path; the help-center home when no article covers it. */
export const resolveHelp = (pathname: string, language: string): { href: string; label: string } => {
  const root = helpCenterRoot(language);
  const article = ARTICLES.find(([pattern]) => matchPath(pattern, pathname))?.[1];
  if (!article) {
    return { href: `${root}/`, label: t('global.help') };
  }
  return { href: `${root}/${article.path}`, label: t('global.helpTopic', { topic: article.topic() }) };
};
